// ============================================================================
// Canonical Egg Dailys route contracts — the hub AND its egg daily record
// ============================================================================
//
// Both surfaces under EggDailysRouter are written in ONE module, for the reason
// `layerDailysPaths.ts` states: `{view: 'eggdailys', paths: ['/layer/eggs/*']}`
// is a single splat row, and the router picks between exactly two shapes on the
// RAW pathname alone:
//
//   /layer/eggs        -> layer.eggDailys.index   (EggDailysHub)
//   /layer/eggs/       -> layer.eggDailys.index   (empty suffix, hub again)
//   /layer/eggs/<id>   -> layer.eggDailys.detail  (EggDailyPage)
//
// The router's own expression is
// `pathname.startsWith('/layer/eggs/') ? pathname.slice(len) || null : null`
// (EggDailysView.tsx), so the index predicate's correctness is a statement
// about what the record predicate does NOT claim, and vice versa.
//
// THE SECOND LAYER DAILY ROUTER. `/layer/dailys/*` and `/layer/eggs/*` are two
// separate splat rows in LAYER_ROUTES, mounting two different routers, and they
// are TRANSITION-FRAME PARTNERS: `routedLocationFor` canonicalizes an unowned
// pathname back to the row's own base, so a frame carrying `view: 'eggdailys'`
// while the browser still shows `/layer/dailys` classifies as
// `layer.eggDailys.index` at the routed pathname `/layer/eggs` — MEASURED
// against the real resolver, not reasoned. That is exactly what the resolver's
// raw-location clause refuses, and the mirror case (`view: 'layerdailys'` at
// `/layer/eggs`) is already refused today by the live `layer.dailys.index`
// clause. Neither router may capture the other's route.
//
// RESERVED SEGMENTS: there are NONE beneath `/layer/eggs/`. `src/lib/routes.ts`
// registers exactly one `/layer/eggs` path (`VIEW_TO_PATH.eggdailys`) and no
// `/layer/eggs/<segment>` route, alias source, alias target, or prefix alias —
// the layer domain appears in neither ALIASES_EXACT nor ALIASES_PREFIX.
// LAYER_ROUTES declares no sibling namespace inside this splat either, and the
// router's id expression special-cases no value. That absence is derived, not
// asserted here: a future `/layer/eggs/archive` row would have to add a
// reserved set to this module AND appear in the static proof that pins the
// three facts above; nothing else would notice it.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const EGG_DAILYS_INDEX_PATHNAME = '/layer/eggs';
export const EGG_DAILYS_DETAIL_PATHNAME_PREFIX = '/layer/eggs/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

function pathnameOf(location: unknown): unknown {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  return (location as LocationLike).pathname;
}

/**
 * The bare hub path, and nothing else.
 *
 * `/layer/eggs/` is excluded deliberately: EggDailysRouter DOES render the hub
 * there (the suffix is empty, so `dailyId` is null), but the splat base is not
 * the audited direct-entry route, and the generic resolver classifies it as
 * this same index surface. Leaving it inside the release would hand a RELEASED
 * verdict to a raw shape no reviewer admitted; the disposition boundary
 * instead supplies explicit recovery.
 */
export function isCanonicalEggDailysIndexPathname(pathname: unknown): pathname is string {
  return pathname === EGG_DAILYS_INDEX_PATHNAME;
}

/**
 * `/layer/eggs/<id>` with exactly one decoded-safe segment.
 *
 * `/layer/eggs` does not start with the prefix and `/layer/eggs/` ends at it,
 * so this predicate cannot claim either hub form. The raw segment is checked
 * BEFORE the decoder so a literal separator, NUL, or line break never reaches
 * it, then the value is decoded once and re-checked so an encoded separator,
 * dot segment, NUL, or line break cannot widen the record release.
 */
export function isCanonicalEggDailysDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(EGG_DAILYS_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }
  const rawSegment = pathname.slice(EGG_DAILYS_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The hub interprets NEITHER search nor hash. `EggDailysHub` calls no location
 * hook at all — the router reads `location.pathname` and hands the hub its
 * projection refresh — and its filter, group and saved-view state is component
 * or persisted state, never URL state. So a presentation-only query or fragment
 * on the canonical hub path does not change this claim, exactly as on the
 * released Layer Dailys index next door.
 */
export function isCanonicalEggDailysIndexLocation(location: unknown): boolean {
  return isCanonicalEggDailysIndexPathname(pathnameOf(location));
}

/**
 * The record page is the opposite case, and the difference is real rather than
 * stylistic: `EggDailyPage` READS `location.hash` to scroll a deep-linked
 * `#comment-<id>` mention into view. A hash-carrying load is therefore a
 * materially different render than the audited one, so both search and hash
 * stay out of the released pair and receive explicit route recovery rather
 * than a broad loader fallback.
 *
 * A bare string is accepted only when it is plainly a pathname, so a caller or
 * test seam cannot smuggle an unsupported query or fragment into the target.
 */
export function isCanonicalEggDailysDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalEggDailysDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalEggDailysDetailPathname(pathname)
  );
}
